/**
 * POSPaymentDialog — modal для платежу + створення чека.
 *
 * Flow:
 *  1. User обирає метод (cash/card/mixed) у parent.
 *  2. Dialog: введення сум, опц. бонусний writeoff (cap = customer.balance).
 *  3. Submit → POST /retail/retail-sales (auto-POST + auto-fiscalize якщо
 *     не is_backdated, але у POS UI завжди не_backdated).
 *  4. Success → showResult з кнопкою «Друкувати» + «Готово».
 */
import { fiscalStatusUk } from '@/shared/utils/fiscalStatus';
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  retailSales,
  type RetailPaymentMethod,
  type RetailSale,
} from '@/api/retailSales';
import type { ReceiptCart } from '@/hooks/pos/useReceiptCart';
import type { CustomerCardLookup } from '@/api/retailCustomerCards';

interface Props {
  cart: ReceiptCart;
  workstationId: string;
  organizationId: string;
  paymentMethod: RetailPaymentMethod;
  customerCard: CustomerCardLookup | null;
  onSuccess: (saleId: string) => void;
  onClose: () => void;
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

const fmt = (v: number) =>
  v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function POSPaymentDialog({
  cart,
  workstationId,
  organizationId,
  paymentMethod,
  customerCard,
  onSuccess,
  onClose,
}: Props) {
  const qc = useQueryClient();
  const total = cart.summary.total;
  const [cashReceived, setCashReceived] = useState(
    paymentMethod === 'mixed' ? '' : total.toFixed(2),
  );
  const [cardAmount, setCardAmount] = useState(
    paymentMethod === 'card' ? total.toFixed(2) : '0',
  );
  const [error, setError] = useState<string | null>(null);
  const [submittedSale, setSubmittedSale] = useState<RetailSale | null>(null);

  // ч.1340: сервер вимагає «готівка + картка = до сплати» (решта — окремо).
  // Карткою — завжди рівно «до сплати» (і після зміни бонусів); змішана — з
  // картки не більше «до сплати», решта — готівкою, решта клієнту — з готівки.
  const isMixed = paymentMethod === 'mixed';
  const isCash = paymentMethod === 'cash';
  const isCard = paymentMethod === 'card';
  const round2 = (v: number) => Math.round(v * 100) / 100;
  const cashEntered = Number(cashReceived) || 0;
  const cardEntered = isCard ? total : (Number(cardAmount) || 0);
  const cashDue = isCash ? total : isMixed ? round2(Math.max(0, total - cardEntered)) : 0;

  const change = useMemo(() => {
    if (isCard) return 0;
    return round2(Math.max(0, cashEntered - cashDue));
  }, [isCard, cashEntered, cashDue]);

  const sufficient = useMemo(() => {
    if (isMixed) {
      return cardEntered > 0 && cardEntered < total - 0.001 && cashEntered >= cashDue - 0.001;
    }
    if (isCash) return cashEntered >= total - 0.001;
    return total > 0;
  }, [isMixed, isCash, cashEntered, cardEntered, cashDue, total]);

  const cashAmountForBackend = cashDue.toFixed(2);
  const cardAmountForBackend = isCash ? '0' : (isCard ? total : cardEntered).toFixed(2);

  const submit = useMutation({
    mutationFn: () =>
      retailSales.create({
        organization_id: organizationId,
        pos_workstation_id: workstationId,
        customer_card_id: customerCard?.id ?? null,
        payment_method: paymentMethod,
        cash_amount: cashAmountForBackend,
        card_amount: cardAmountForBackend,
        bonus_writeoff_amount: cart.bonusWriteoff.toFixed(2),
        change_amount: change.toFixed(2),
        discount_amount: cart.discountAmount.toFixed(2),
        is_backdated: false,
        items: cart.items.map((it, idx) => ({
          line_number: idx + 1,
          nomenclature_id: it.nomenclature_id,
          item_kind: it.item_kind,
          quantity: it.quantity.toString(),
          price_per_unit: it.price_per_unit.toFixed(2),
          vat_rate: it.vat_rate.toFixed(2),
        })),
      }),
    onSuccess: (sale) => {
      setSubmittedSale(sale);
      qc.invalidateQueries({ queryKey: ['pos-current-shift'] });
    },
    onError: (e) => setError(extractError(e)),
  });

  // Автоматична фіскалізація після створення (якщо чек posted але fiscal=pending)
  const fiscalize = useMutation({
    mutationFn: () => retailSales.fiscalize(submittedSale!.id),
    onSuccess: (r) => setSubmittedSale(r.document),
    onError: (e) => setError(extractError(e)),
  });

  return (
    <div className="pos-modal-overlay">
      <div className="pos-modal" style={{ maxWidth: 500 }}>
        <div className="pos-modal__header">
          <span>
            {paymentMethod === 'cash' && '💵 Готівка'}
            {paymentMethod === 'card' && '💳 Картка'}
            {paymentMethod === 'mixed' && '🎁 Змішана'}
          </span>
          {!submittedSale && (
            <button className="pos-modal__btn-secondary" onClick={onClose}>
              ✕
            </button>
          )}
        </div>
        <div className="pos-modal__body">
          {!submittedSale ? (
            <>
              <div className="pos-modal__amount-display">До сплати: {fmt(total)}</div>

              {(isCash || isMixed) && (
                <div className="pos-modal__field">
                  <label>Готівка отримано</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    autoFocus
                  />
                </div>
              )}
              {(!isCash) && (
                <div className="pos-modal__field">
                  <label>Картка</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={isCard ? total.toFixed(2) : cardAmount}
                    readOnly={isCard}
                    onChange={(e) => setCardAmount(e.target.value)}
                  />
                </div>
              )}
              {isMixed && (
                <div className="pos-modal__field">
                  <label>Готівкою до сплати</label>
                  <div style={{ fontWeight: 700 }}>{fmt(cashDue)} грн</div>
                </div>
              )}
              {!isCard && change > 0 && (
                <div className="pos-modal__field">
                  <label>Решта (видача клієнту)</label>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--primary)' }}>
                    {fmt(change)} грн
                  </div>
                </div>
              )}
              {customerCard && (
                <div className="pos-modal__field">
                  <label>
                    Списати бонусів (макс. {fmt(Number(customerCard.bonus_balance))})
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={Number(customerCard.bonus_balance)}
                    value={cart.bonusWriteoff}
                    onChange={(e) => {
                      const v = Math.min(
                        Number(e.target.value) || 0,
                        Number(customerCard.bonus_balance),
                      );
                      cart.setBonusWriteoff(v);
                    }}
                  />
                </div>
              )}
              {!sufficient && (
                <div style={{ color: 'var(--warning)', padding: '0.5rem 0' }}>
                  {isMixed && (cardEntered <= 0 || cardEntered >= total - 0.001)
                    ? '⚠ Для змішаної оплати сума карткою має бути більша за нуль і менша за «до сплати»'
                    : '⚠ Сума оплати менша за загальну'}
                </div>
              )}
              {error && (
                <div style={{ color: 'var(--danger)', padding: '0.5rem 0' }}>
                  {error}
                </div>
              )}
            </>
          ) : (
            <>
              <h3 style={{ marginTop: 0, color: 'var(--success)' }}>✓ Чек створено</h3>
              <div className="pos-modal__amount-display">{submittedSale.document_number}</div>
              <div className="pos-modal__field">
                Сума: <strong>{fmt(Number(submittedSale.total_amount))} грн</strong>
              </div>
              {submittedSale.change_amount && Number(submittedSale.change_amount) > 0 && (
                <div className="pos-modal__field">
                  Решта клієнту:{' '}
                  <strong style={{ fontSize: '1.2rem' }}>
                    {fmt(Number(submittedSale.change_amount))} грн
                  </strong>
                </div>
              )}
              <div className="pos-modal__field">
                ПРРО:{' '}
                <strong>{fiscalStatusUk(submittedSale.fiscal_status)}</strong>
                {submittedSale.fiscal_number && <> · № {submittedSale.fiscal_number}</>}
              </div>
              {error && (
                <div style={{ color: 'var(--danger)', padding: '0.5rem 0' }}>{error}</div>
              )}
            </>
          )}
        </div>
        <div className="pos-modal__footer">
          {!submittedSale ? (
            <>
              <button className="pos-modal__btn-secondary" onClick={onClose}>
                Скасувати
              </button>
              <button
                className="pos-modal__btn-primary"
                onClick={() => submit.mutate()}
                disabled={!sufficient || submit.isPending || cart.isEmpty}
              >
                {submit.isPending ? 'Обробляю...' : '✓ ПIДТВЕРДИТИ'}
              </button>
            </>
          ) : (
            <>
              {submittedSale.fiscal_status === 'pending' && (
                  <button
                    className="pos-modal__btn-secondary"
                    onClick={() => fiscalize.mutate()}
                    disabled={fiscalize.isPending}
                  >
                    📋 Фіскалізувати
                  </button>
                )}
              <button
                className="pos-modal__btn-secondary"
                onClick={() => window.open(`/pos/receipt/${submittedSale.id}/print`, '_blank')}
              >
                🖨 Друк
              </button>
              <button
                className="pos-modal__btn-primary"
                onClick={() => onSuccess(submittedSale.id)}
              >
                Готово
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
